import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/custom/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  MultiStepDialogContent,
  DialogPage,
} from "@/components/custom/paginated-dialog-content/multi-step-dialog-content";
import { client } from "@/client";
import {
  normalizeSiteCsvHeaders,
  SITE_CSV_OPTIONAL,
  SITE_CSV_REQUIRED,
} from "./-upload-sites-headers";

import { showActionErrorToast } from "@/state/error-toast";

const uploadSitesSchema = z.object({
  file: z.custom<File>((v) => v instanceof File, {
    message: "File is required",
  }),
});

interface ClientValidationResult {
  isValid: boolean;
  recordCount: number;
  foundFields: string[];
  missingFields: string[];
  extraFields: string[];
  errors: string[];
}

interface SiteUploadResult {
  dryRun: boolean;
  results: {
    name: string;
    region?: string;
    subRegion?: string;
    group?: string;
    activationTime?: string | Date;
    tags?: string[];
    status: "new" | "existing" | "error";
    error?: string;
    // ID-7057: non-blocking notices, e.g. a region that will be created or a sub-region that
    // will be cleared. Optional here so an older backend response still narrows.
    warnings?: string[];
  }[];
  totalNew: number;
  totalExisting: number;
  totalErrors: number;
}

export const UploadSitesDialog = ({
  onClose,
}: {
  onClose?: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const queryClient = useQueryClient();
  // We'll store the dryRun result here
  const [dryRunResult, setDryRunResult] = useState<SiteUploadResult | null>(
    null,
  );

  const form = useForm<z.infer<typeof uploadSitesSchema>>({
    resolver: zodResolver(uploadSitesSchema),
    defaultValues: {
      file: undefined,
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (data: { file: File; dryRun: boolean }) =>
      client.sites.uploadCSV({
        formData: { file: data.file, dryRun: data.dryRun },
      }),
    onSuccess: (data, variables) => {
      if (variables.dryRun) {
        setDryRunResult(data);
        setCurrentStep(3); // Move to Server Verification Step (Page 4)
      } else {
        // Final upload success
        setDryRunResult(data); // Store data for result page
        toast.success(t("Sites uploaded successfully"));
        queryClient.invalidateQueries({
          queryKey: client.sites.listSummaries.queryKey,
        });
        // The upload creates or reactivates regions and sub-regions named in the file (the
        // region resolver), so every list that shows them is stale: the site dropdowns and Data
        // Review filters (GET /sites/regions|subRegions) and the Settings > Regions table.
        queryClient.invalidateQueries({
          queryKey: client.sites.getRegions.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: client.sites.getSubRegions.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: client.regions.listSummaries.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: client.regions.listRegionOptions.queryKey,
        });
        queryClient.invalidateQueries({
          queryKey: client.regions.listSubRegionOptions.queryKey,
        });
        setCurrentStep(4); // Move to Results Step (Page 5)
      }
    },
    onError: (error: any) => {
      showActionErrorToast(error, t("Upload failed"));
    },
  });

  const file = form.watch("file");

  // Client-Side Validation State
  const [clientValidationState, setClientValidationState] = useState<
    "idle" | "validating" | "valid" | "invalid"
  >("idle");
  const [clientValidationResult, setClientValidationResult] =
    useState<ClientValidationResult | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        form.setValue("file", acceptedFiles[0], { shouldValidate: true });
        // Reset validation when file changes
        setClientValidationState("idle");
        setClientValidationResult(null);
        setDryRunResult(null);
      }
    },
    [form],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const validateFileClientSide = async (fileToValidate: File) => {
    setClientValidationState("validating");
    setClientValidationResult(null);

    const result: ClientValidationResult = {
      isValid: false,
      recordCount: 0,
      foundFields: [],
      missingFields: [],
      extraFields: [],
      errors: [],
    };

    // Basic file type check
    const isCsv =
      fileToValidate.type === "text/csv" ||
      fileToValidate.name.toLowerCase().endsWith(".csv");

    if (!isCsv) {
      result.errors.push(t("File must be a CSV file."));
      setClientValidationResult(result);
      setClientValidationState("invalid");
      return false;
    }

    try {
      const text = await fileToValidate.text();
      const lines = text
        .split(/\r\n|\n/)
        .filter((line) => line.trim().length > 0);

      if (lines.length === 0) {
        result.errors.push(t("File is empty."));
        setClientValidationResult(result);
        setClientValidationState("invalid");
        return false;
      }

      const headerLine = lines[0];
      // Normalised the way the backend parser normalises (BOM, case, aliases) -- see the module.
      const headers = normalizeSiteCsvHeaders(headerLine);

      const hasName = headers.includes("name");

      if (!hasName) {
        result.errors.push(t('Missing required column: "name"'));
      } else {
        result.foundFields.push("name");
      }

      // Check optional fields
      const optionalFields: string[] = [...SITE_CSV_OPTIONAL];
      const knownFields = [...SITE_CSV_REQUIRED, ...optionalFields];

      optionalFields.forEach((field) => {
        if (headers.indexOf(field) !== -1) {
          result.foundFields.push(field);
        } else {
          result.missingFields.push(field);
        }
      });

      // Check for extra fields
      headers.forEach((header) => {
        if (!knownFields.includes(header)) {
          result.extraFields.push(header);
        }
      });

      result.recordCount = lines.length - 1; // Exclude header

      if (result.errors.length > 0) {
        setClientValidationResult(result);
        setClientValidationState("invalid");
        // The step gate relies on this boolean (the state set above is not visible to it until the
        // next render), so it has to say INVALID -- `true` here sent a file with no `name` column
        // to the server dry run.
        return false;
      }

      result.isValid = true;
      setClientValidationResult(result);
      setClientValidationState("valid");
      return true;
    } catch (error) {
      console.error("Error reading file:", error);
      result.errors.push(t("Error reading file."));
      setClientValidationResult(result);
      setClientValidationState("invalid");
      return false;
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = "name,region,subRegion,group,activationTime,tags";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "sites_template.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = (data: z.infer<typeof uploadSitesSchema>) => {
    // Final upload
    uploadMutation.mutate({ file: data.file, dryRun: false });
  };

  // --- Pages ---

  // Page 1: Preparation
  const Page1 = useMemo(
    () => (
      <div className="space-y-4 pt-2">
        <div className="text-left space-y-4">
          <h3 className="text-lg font-medium">
            {t("How to prepare your file")}
          </h3>

          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Important Notes:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>{t("The Name field is mandatory to identify the site.")}</li>
              <li>{t("Tags are merged (separated by ';').")}</li>
              <li>{t("Other fields will overwrite existing values.")}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Required Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              <li>{t("name - Site Name")}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium mb-2 text-sm">
              {t("Optional Fields:")}
            </h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              {/* The header names the file must carry, with the label they mean. */}
              <li>
                <code>region</code> — {t("region")}
              </li>
              <li>
                <code>subRegion</code> — {t("subRegion")}
              </li>
              <li>
                <code>group</code> — {t("siteGroup")}
              </li>
              <li>
                <code>activationTime</code> — {t("activationTime")}
              </li>
              <li>
                <code>tags</code> — {t("tags")}
              </li>
            </ul>
          </div>

          <div className="pt-2 space-y-2">
            <p className="text-sm text-muted-foreground text-center">
              {t("Need formatting help? Download our template CSV file")}
            </p>
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={handleDownloadTemplate}
              >
                <Download className="h-4 w-4" />
                {t("Download Template")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    ),
    [t],
  );

  // Page 2: Select File
  const Page2 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Select File")}</h3>
        <FormField
          control={form.control}
          name="file"
          render={() => (
            <FormItem>
              <FormControl>
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-lg p-10 text-center cursor-pointer hover:bg-accent/50 transition-colors flex flex-col items-center justify-center gap-4 min-h-[200px]",
                    isDragActive
                      ? "border-azure bg-azure/5"
                      : "border-muted-foreground/25",
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="rounded-full bg-muted p-4">
                    <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    {file ? (
                      <>
                        <p className="text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("changeFile")}
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          {t("Drag and drop your file here")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("or click to browse")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {t("Only CSV files are allowed")}
                        </p>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          className="mt-2"
                        >
                          {t("Browse")}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    ),
    [file, getRootProps, getInputProps, isDragActive, t],
  );

  // Page 3: Client Validation
  const Page3 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">{t("Client Validation")}</h3>

        {clientValidationState === "validating" && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-azure" />
            <p className="text-sm text-muted-foreground">
              {t("Validating file format...")}
            </p>
          </div>
        )}

        {clientValidationState === "invalid" && (
          <div className="rounded-md bg-red-50 p-4 border border-red-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle
                  className="h-5 w-5 text-red-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {t("Validation Failed")}
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {clientValidationResult?.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {clientValidationState === "valid" && clientValidationResult && (
          <div className="space-y-4">
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle2
                    className="h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    {t("Validation Successful")}
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>{t("File format is correct (CSV)")}</li>
                      {clientValidationResult.foundFields.includes("name") && (
                        <li>{t('Found column: "name"')}</li>
                      )}
                      <li>
                        {t('Found "{{count}}" records', {
                          count: clientValidationResult.recordCount,
                        })}
                      </li>
                      {clientValidationResult.foundFields
                        .filter((f) => f !== "name")
                        .map((field) => (
                          <li key={field}>
                            {t('Found column: "{{field}}"', { field })}
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {clientValidationResult.missingFields.length > 0 && (
              <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-amber-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-800">
                      {t("Missing Optional Fields")}
                    </h3>
                    <div className="mt-2 text-sm text-amber-700">
                      <p className="mb-2">
                        {t(
                          "The following optional fields were not found and will not be updated:",
                        )}
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        {clientValidationResult.missingFields.map((field) => (
                          <li key={field}>{field}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {clientValidationResult.extraFields.length > 0 && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Unknown Fields Found")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p className="mb-2 text-xs font-medium">
                        {t(
                          "The following fields are not recognized and will be ignored:",
                        )}
                      </p>
                      <ul className="list-disc pl-5 space-y-1">
                        {clientValidationResult.extraFields.map((field) => (
                          <li key={field} className="font-mono">
                            {field}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [clientValidationState, clientValidationResult, t],
  );

  // Helper to render result items
  const renderResultItems = (result: SiteUploadResult) => (
    <div className="max-h-[300px] overflow-y-auto border rounded-md">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Name")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Status")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t("Details")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {result.results.map((item, idx) => (
            <tr key={idx}>
              <td
                className={cn(
                  "px-3 py-2 whitespace-nowrap text-sm font-medium",
                  item.status === "new" ? "text-green-600" : "text-black",
                )}
              >
                {item.name}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                {item.status === "new" ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {t("New")}
                  </span>
                ) : item.status === "existing" ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {t("Update")}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    {t("Error")}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                {item.error ? (
                  <span className="text-red-500">{item.error}</span>
                ) : (
                  // Every value this row will apply, not just the region. The CSV upload is the
                  // only region write path during the migration freeze, so a change the
                  // confirmation step does not show is a change the user never agreed to -- an
                  // existing group or sub-region carries no warning and would otherwise be
                  // invisible.
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-xs">
                    {item.region && (
                      <span>
                        <span className="text-gray-400">{t("region")}: </span>
                        {item.region}
                      </span>
                    )}
                    {item.subRegion && (
                      <span>
                        <span className="text-gray-400">
                          {t("subRegion")}:{" "}
                        </span>
                        {item.subRegion}
                      </span>
                    )}
                    {item.group && (
                      <span>
                        <span className="text-gray-400">
                          {t("siteGroup")}:{" "}
                        </span>
                        {item.group}
                      </span>
                    )}
                    {item.activationTime && (
                      <span>
                        <span className="text-gray-400">
                          {t("activationTime")}:{" "}
                        </span>
                        {String(item.activationTime)}
                      </span>
                    )}
                    {item.tags && item.tags.length > 0 && (
                      <span className="text-gray-400">
                        [{item.tags.join(", ")}]
                      </span>
                    )}
                  </div>
                )}
                {item.warnings && item.warnings.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {item.warnings.map((warning) => (
                      <li key={warning} className="text-xs text-amber-600">
                        {warning}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Page 4: Server Verification (Dry Run Results)
  const Page4 = useMemo(
    () => (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">
          {t("Server Verification Results")}
        </h3>
        {uploadMutation.isPending && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-azure" />
            <p className="text-sm text-muted-foreground">
              {t("Verifying with server...")}
            </p>
          </div>
        )}
        {!uploadMutation.isPending && dryRunResult && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-green-600">
                  {dryRunResult.totalNew}
                </div>
                <div className="text-xs text-green-800">{t("New Sites")}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-gray-600">
                  {dryRunResult.totalExisting}
                </div>
                <div className="text-xs text-gray-800">
                  {t("To Be Updated")}
                </div>
              </div>
              <div className="bg-red-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-red-600">
                  {dryRunResult.totalErrors}
                </div>
                <div className="text-xs text-red-800">{t("Errors")}</div>
              </div>
            </div>

            {renderResultItems(dryRunResult)}

            {dryRunResult.totalErrors > 0 && (
              <div className="rounded-md bg-red-50 p-4 border border-red-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {t("Validation Errors")}
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        {t(
                          "Please fix the errors in your CSV file before uploading.",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    ),
    [uploadMutation.isPending, dryRunResult, t],
  );

  // Page 5: Final Results
  const Page5 = useMemo(
    () => (
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <div className="inline-flex rounded-full bg-green-100 p-3">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-green-800">
            {t("Upload Successful!")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("Sites have been processed successfully.")}
          </p>
        </div>

        {dryRunResult && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-green-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-green-600">
                  {dryRunResult.totalNew}
                </div>
                <div className="text-xs text-green-800">{t("Created")}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-gray-600">
                  {dryRunResult.totalExisting}
                </div>
                <div className="text-xs text-gray-800">{t("Updated")}</div>
              </div>
              <div className="bg-red-50 p-3 rounded-md text-center">
                <div className="text-xl font-bold text-red-600">
                  {dryRunResult.totalErrors}
                </div>
                <div className="text-xs text-red-800">{t("Failed")}</div>
              </div>
            </div>

            {renderResultItems(dryRunResult)}
          </div>
        )}
      </div>
    ),
    [t, dryRunResult],
  );

  const pages: DialogPage[] = useMemo(
    () => [
      {
        title: t("Preparation"),
        content: Page1,
      },
      {
        title: t("Select File"),
        content: Page2,
        disableNext: !file,
        blocker: async () => {
          // Validate client side before moving to next step
          if (!file) return false;
          await validateFileClientSide(file);
          // We allow moving to the validation page even if invalid?
          // No, the validation display is ON the next page (Page 3).
          // So we should just proceed to Page 3 which runs the validation visualization?
          // Or run it here?
          // Let's run it here, and if valid, we move to Page 3 to SHOW it's valid?
          // Actually, usually "Validation" step implies the process happens there.
          // Let's make Page 3 run the validation on mount or entry?
          // Re-reading Reference: UploadCatalogDialog runs validation in "blocker" of Upload Step
          // passing to Validation Step.
          return true;
        },
      },
      {
        title: t("Client Validation"),
        content: Page3,
        // Trigger validation when entering this step?
        // Or we already ran it.
        // Let's use `blocker` to prevent moving to Server Verification if client validation failed.
        blocker: async () => {
          if (clientValidationState !== "valid") {
            // The state validateFileClientSide sets is not visible in this closure until the next
            // render, so re-reading it here saw the OLD value; the boolean it returns is current.
            const ok = file ? await validateFileClientSide(file) : false;
            if (!ok) return false;
          }

          // If valid, trigger Server Dry Run
          if (file) {
            // Trigger dry run mutation
            uploadMutation.mutate({ file, dryRun: true });
            // We return false here to prevent immediate step advance,
            // because mutation is async and we want to advance ONLY on success.
            // The mutation onSuccess handles the step advancement.
            return false;
          }
          return false;
        },
        hideNext: clientValidationState === "invalid",
      },
      {
        title: t("Server Verification"),
        content: Page4,
        blocker: () => {
          if (uploadMutation.isPending) return false;
          // Block if there are specific errors that should prevent upload
          if (dryRunResult && dryRunResult.totalErrors > 0) return false;

          // Trigger final upload
          if (file) {
            uploadMutation.mutate({ file, dryRun: false });
            return false; // Wait for onSuccess to advance
          }
          return false;
        },
        disableNext:
          uploadMutation.isPending ||
          (dryRunResult ? dryRunResult.totalErrors > 0 : false),
        // Next button here essentially means "Confirm & Upload"
      },
      {
        title: t("Results"),
        content: Page5,
        hideNext: true,
        hideBack: true,
        footer: (
          <Button variant="outline" onClick={onClose}>
            {t("Done")}
          </Button>
        ),
      },
    ],
    [
      Page1,
      Page2,
      Page3,
      Page4,
      Page5,
      t,
      file,
      clientValidationState,
      uploadMutation,
      onClose,
    ],
  );

  const handleNext = useCallback(async () => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep === 4) {
      onClose?.();
    } else {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep, onClose]);

  return (
    <div className="w-[600px] min-h-[500px] flex flex-col">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex-1 flex flex-col h-full"
        >
          <MultiStepDialogContent
            className="flex-1"
            title={t("Upload Sites CSV")}
            pages={pages}
            currentStep={currentStep}
            onNext={handleNext}
            onBack={handleBack}
            onSubmit={() => handleSubmit(form.getValues())}
            finishButtonText={t("Upload")}
          />
        </form>
      </Form>
    </div>
  );
};
